<template>
	<div class="recap-section">
		<h5 class="font-weight-bold mb-3">{{ $t('monte.recap_title') }}</h5>

		<div class="recap-timeline">

			<!-- Saison / Jument / Étalon -->
			<div class="recap-item">
				<div v-if="season && season.season_label" class="recap-line">
					<span class="text-muted">{{ $t('monte.saison') }} :</span>
					<strong>{{ season.season_label }}</strong>
				</div>
				<div v-if="jument" class="recap-line mt-1">
					<span class="text-muted">{{ $t('monte.jument') }} :</span>
					<strong>{{ jument.horse_nom }}</strong>
				</div>
				<div v-if="stallion" class="recap-line mt-1">
					<span class="text-muted">{{ $t('monte.etalon') }} :</span>
					<strong>{{ stallion.horse_nom }}</strong>
				</div>
			</div>

			<!-- Lieu de monte -->
			<div v-if="cmep && cmep.tiers_id" class="recap-item">
				<p class="text-muted mb-1">{{ $t('monte.cmep') }} :</p>
				<address class="mb-0 small">
					<strong>{{ cmep.tiers_rs }}</strong><br>
					<span v-if="cmep.tiers_address1">{{ cmep.tiers_address1 | multiCapitalize }}<br></span>
					<span v-if="cmep.tiers_address2">{{ cmep.tiers_address2 | multiCapitalize }}<br></span>
					<span v-if="cmep.tiers_postalcode || cmep.tiers_town">
						<span v-if="cmep.tiers_postalcode">{{ cmep.tiers_postalcode }} </span><span v-if="cmep.tiers_town">{{ cmep.tiers_town | multiCapitalize }}</span><br>
					</span>
					<span v-if="cmep.tiers_pays">{{ cmep.tiers_pays | uppercase }}</span>
				</address>
			</div>

			<!-- Condition de monte -->
			<div v-if="type_monte" class="recap-item">
				<p class="text-muted mb-1">{{ $t('monte.conditions_monte') }} :</p>
				<p class="mb-1"><strong>{{ type_monte.name }}</strong></p>
				<p v-if="type_monte.type_monte && ['iac', 'iarr', 'icsi', 'vp'].includes(type_monte.type_monte)" class="mb-1 small">
					{{ $t('monte.nombre_paillette') }} : <strong>{{ type_monte.paillettes }}</strong>
				</p>
				<template v-if="type_monte.articles && type_monte.articles.length > 0">
					<div
						v-for="(article, idx) in type_monte.articles"
						:key="'recap_article_' + idx"
						class="d-flex justify-content-between small mt-1"
					>
						<span class="text-muted">{{ article.articles_label }}</span>
						<span class="ml-2 text-right">
							<strong>{{ formatPrice(article.articles_ht) }}</strong>
							<span class="text-muted ml-1">{{ $t('monte.ht') }}</span>
						<br><em class="text-muted">{{ formatPrice(article.articles_ttc) }} {{ $t('monte.ttc') }}</em>
						</span>
					</div>
				</template>
				<p v-if="contractSaved" class="mb-0 mt-1">
					<button type="button" class="btn btn-link p-0" @click.prevent="$emit('preview')">
						{{ $t('monte.preview_contrat') }}
					</button>
				</p>
			</div>

			<!-- Facturation -->
			<div v-if="tiers && tiers.tiers_id" class="recap-item">
				<p class="text-muted mb-1">{{ $t('monte.facturation') }} :</p>
				<address class="mb-0 small">
					<strong>{{ tiers.tiers_rs }}</strong><br>
					<span v-if="tiers.tiers_address1">{{ tiers.tiers_address1 }}<br></span>
					<span v-if="tiers.tiers_address2">{{ tiers.tiers_address2 }}<br></span>
					<span v-if="tiers.tiers_postalcode || tiers.tiers_town">{{ [tiers.tiers_postalcode, tiers.tiers_town].filter(Boolean).join(' ') }}<br></span>
					<span v-if="tiers.tiers_country">{{ getCountryLabel(tiers.tiers_country) }}<br></span>
					<span v-if="tiers.tiers_vat_number">TVA : {{ tiers.tiers_vat_number }}<br></span>
					<span v-if="tiers.tiers_siret">SIRET : {{ tiers.tiers_siret }}</span>
				</address>
			</div>

		</div>
	</div>
</template>

<script>
import Tools from '@/mixins/Tools.js'
import Countries from '@/assets/lang/countries/countries'
import Vue from 'vue'

export default {
	name: 'RecapSection',
	mixins: [Tools],
	methods: {
		/**
		 * Retourne le libellé traduit d'un pays selon la locale courante.
		 * @param {string} id - Code pays ISO (ex: 'FR').
		 * @returns {string} Libellé du pays ou le code brut si non trouvé.
		 */
		getCountryLabel(id) {
			if(!id) return ''
			const labels = Countries[Vue.i18n.locale()]
			return (labels && labels.label && labels.label[id]) || id
		},
		/**
		 * Formate un montant en euros selon la locale courante.
		 * @param {number|null} value - Montant en euros (déjà converti depuis centimes).
		 * @returns {string} Chaîne formatée en devise (ex: "1 200,00 €"), ou chaîne vide.
		 */
		formatPrice(value) {
			if(value === null || value === undefined) return ''
			return this.priceFormat(value, 'EUR', true)
		},
		/**
		 * Détermine si la TVA s'applique sur un article de contrat.
		 * Les codes FDVXP (intracom UE) et FDVEX (export hors UE) sont exonérés.
		 * @param {Object} article - Article avec propriété vat_code.
		 * @returns {boolean} true si la TVA s'applique.
		 */
		// TVA s'applique si vat_code = FD1V (ou pas encore de compte client créé)
		// FDVXP (CEE intracom) ou FDVEX (export hors CEE) = exonéré
		tvaApplies(article) {
			return !article.vat_code || article.vat_code === 'FD1V'
		},
	},
	props: {
		season:       { type: Object, default: () => null },
		stallion:     { type: Object, default: () => null },
		jument:       { type: Object, default: () => null },
		cmep:         { type: Object, default: () => null },
		type_monte:   { type: Object, default: () => null },
		tiers:        { type: Object, default: () => null },
		contractSaved:{ type: Boolean, default: false },
	},
}
</script>

<style scoped>
.recap-timeline {
	padding-left: 0.5rem;
}

.recap-item {
	position: relative;
	padding-left: 1.25rem;
	padding-bottom: 1rem;
}

/* Puce */
.recap-item::before {
	content: '';
	position: absolute;
	left: -1px;
	transform: translateX(-50%);
	top: 0.35rem;
	width: 0.75rem;
	height: 0.75rem;
	border-radius: 50%;
	background: var(--primary);
}

/* Ligne verticale — démarre sous la puce (top = dot top + dot height + gap) */
.recap-item::after {
	content: '';
	position: absolute;
	left: -1px;
	transform: translateX(-50%);
	top: calc(0.35rem + 0.75rem + 0.25rem);
	bottom: 0;
	width: 2px;
	background: var(--primary);
}

.recap-item:last-of-type::after {
	display: none;
}

.recap-line {
	display: flex;
	align-items: baseline;
	gap: 0.25rem;
	color: var(--primary);
}

.recap-line .text-muted {
	flex-shrink: 0;
}

.recap-article {
	border-top: 1px solid #f0f0f0;
	padding-top: 0.5rem;
}
</style>
